import React, { useState, useEffect } from 'react';
import { db } from '../firebase';
import { collection, query, getDocs, orderBy, where } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { motion } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner, faDownload, faCheckCircle, faTimesCircle, faCreditCard } from '@fortawesome/free-solid-svg-icons';

const PaymentHistory = () => {
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const auth = getAuth();
  const user = auth.currentUser;

  useEffect(() => {
    const fetchPaymentHistory = async () => {
      if (!user) {
        setLoading(false);
        return;
      }

      try {
        // Query the transactions collection instead of the nested collection
        const q = query(
          collection(db, 'transactions'),
          where('userId', '==', user.uid),
          orderBy('timestamp', 'desc')
        );
        
        const querySnapshot = await getDocs(q);
        const historyData = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
          // Convert Firestore timestamp to JS Date
          date: doc.data().timestamp?.toDate() || new Date(),
        }));
        
        setPaymentHistory(historyData);
      } catch (error) {
        console.error('Error fetching payment history:', error);
        setError('Failed to load payment history. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchPaymentHistory();
  }, [user]);

  // Format date for display
  const formatDate = (date) => {
    return date instanceof Date 
      ? date.toLocaleDateString('en-US', { 
          year: 'numeric', 
          month: 'short', 
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        })
      : 'Unknown date';
  };

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.1 } },
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: 'easeOut' } },
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'successful':
        return <FontAwesomeIcon icon={faCheckCircle} className="text-green-500 mr-2" />;
      case 'failed':
        return <FontAwesomeIcon icon={faTimesCircle} className="text-red-500 mr-2" />;
      case 'pending':
        return <FontAwesomeIcon icon={faSpinner} className="text-yellow-500 animate-spin mr-2" />;
      default:
        return <FontAwesomeIcon icon={faCreditCard} className="text-gray-500 mr-2" />;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-100 to-gray-200">
        <div className="flex items-center space-x-2 text-gray-600">
          <FontAwesomeIcon icon={faSpinner} className="animate-spin text-2xl" />
          <span className="text-lg font-semibold">Loading payment history...</span>
        </div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-100 to-gray-200">
        <div className="bg-red-100 p-6 rounded-lg shadow-md text-center max-w-md">
          <FontAwesomeIcon icon={faTimesCircle} className="text-red-500 text-3xl mb-3" />
          <p className="text-red-700 mb-4">{error}</p>
          <button 
            onClick={() => window.location.reload()}
            className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <motion.div
      className="min-h-screen bg-gradient-to-br from-gray-100 to-gray-200 font-['Noto Sans'] py-12 px-4 sm:px-6 lg:px-8"
      initial="hidden"
      animate="visible"
      variants={containerVariants}
    >
      <div className="max-w-7xl mx-auto">
        <h2 className="text-4xl font-extrabold text-center mb-10 bg-clip-text text-transparent bg-gradient-to-r from-purple-600 to-blue-500">
          <FontAwesomeIcon icon={faCreditCard} className="mr-3 text-purple-600" />
          Payment History
        </h2>

        {paymentHistory.length === 0 ? (
          <div className="bg-white rounded-2xl shadow-lg p-8 text-center border border-gray-200">
            <p className="text-gray-500 text-lg font-medium">No payment history available.</p>
            <p className="text-gray-400 mt-2">When you make purchases, they will appear here.</p>
          </div>
        ) : (
          <motion.div className="space-y-4" variants={containerVariants}>
            <div className="overflow-x-auto">
              <table className="min-w-full bg-white rounded-xl shadow-lg overflow-hidden">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Plan</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Credits</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {paymentHistory.map((record) => (
                    <motion.tr 
                      key={record.id}
                      variants={cardVariants}
                      className="hover:bg-gray-50"
                    >
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatDate(record.date)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {record.planId || 'Unknown Plan'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {record.currency ? `${record.amount || 0} ${record.currency.toUpperCase()}` : 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        +{record.credits || 0}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        <span className="flex items-center">
                          {getStatusIcon(record.status)}
                          {record.status ? record.status.charAt(0).toUpperCase() + record.status.slice(1) : 'Unknown'}
                        </span>
                      </td>
                    </motion.tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            <div className="bg-blue-50 rounded-lg p-4 mt-4 text-sm text-blue-700">
              <p className="font-medium">Need a receipt?</p>
              <p>Contact support with your transaction ID to request a receipt.</p>
            </div>
          </motion.div>
        )}
      </div>
    </motion.div>
  );
};

export default PaymentHistory;